import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import type { DiagnosisViewModel } from '@/features/patient-record/types';

import { SectionHeading } from './SummaryItem';

const VISIBLE_LIMIT = 8;

interface DiagnosesTooltipProps {
  readonly confirmed: readonly DiagnosisViewModel[];
  readonly suspected: readonly DiagnosisViewModel[];
}

export function DiagnosesTooltip({
  confirmed,
  suspected,
}: DiagnosesTooltipProps): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="space-y-2 min-w-[260px]">
      {confirmed.length > 0 && (
        <DiagnosisGroup
          heading={t('patient.geneticSummary.tooltips.confirmedTitle', {
            count: confirmed.length,
          })}
          diagnoses={confirmed}
          dotClassName="bg-primary"
        />
      )}
      {suspected.length > 0 && (
        <>
          {confirmed.length > 0 && <hr className="border-border" />}
          <DiagnosisGroup
            heading={t('patient.geneticSummary.tooltips.suspectedTitle', {
              count: suspected.length,
            })}
            diagnoses={suspected}
            dotClassName="bg-amber-500"
          />
        </>
      )}
    </div>
  );
}

interface DiagnosisGroupProps {
  readonly heading: string;
  readonly diagnoses: readonly DiagnosisViewModel[];
  readonly dotClassName: string;
}

function DiagnosisGroup({
  heading,
  diagnoses,
  dotClassName,
}: DiagnosisGroupProps): ReactElement {
  const { t } = useTranslation();
  const visible = diagnoses.slice(0, VISIBLE_LIMIT);
  const overflow = diagnoses.length - visible.length;

  return (
    <div className="space-y-1">
      <SectionHeading text={heading} />
      <ul className="space-y-0.5">
        {visible.map((dx) => (
          <li key={dx.id} className="flex items-center gap-1.5 text-xs">
            <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', dotClassName)} />
            <span className="truncate text-foreground">{dx.title}</span>
            <span className="text-muted-foreground/80 ml-auto text-[10px] shrink-0 tabular-nums">
              {dx.termId}
            </span>
          </li>
        ))}
      </ul>
      {overflow > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {t('patient.geneticSummary.tooltips.andMore', { count: overflow })}
        </p>
      )}
    </div>
  );
}
